import { useCallback, useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import type { DateTimePickerProps } from 'react-flatpickr';
import Flatpickr from 'react-flatpickr';
import { usePopper } from 'react-popper';

// Types
import type PopperJS from '@popperjs/core';
import clsx from 'clsx';

// Context
import useToggle from '@/utils/hooks/utils/useToggle';

// Components
import SingleDatepickerInput from '@/components/base/Datepicker/SingleDatepickerInput';

type ChildrenTimePickerParamsType = {
    ref: (node: HTMLInputElement) => void;
    toggleDatepicker: () => Promise<void>;
    setReferenceElement: React.Dispatch<React.SetStateAction<HTMLButtonElement>>;
};

type TimepickerPropsType = {
    time?: string;
    onChange?: (time: Date) => void;
    placement?: PopperJS.Placement;
    hasBorder?: boolean;
    options?: DateTimePickerProps['options'];
    afterInput?: React.ReactNode;
    disabled?: boolean;
    fullWidth?: boolean;
    children?: (args: ChildrenTimePickerParamsType) => React.ReactElement;
    portalContainer?: Element | DocumentFragment;
};

const Timepicker = ({
    time = null,
    onChange,
    placement = 'bottom-start',
    hasBorder = false,
    options = {},
    afterInput = null,
    disabled = false,
    fullWidth = false,
    children,
    portalContainer
}: TimepickerPropsType) => {
    const { isOpen, toggle } = useToggle();

    // Refs
    const timepickerRef = useRef<Flatpickr | null>(null);
    const timepickerWrapperRef = useRef<HTMLDivElement | null>(null);
    const [timepickerRefInit, setTimepickerRefInit] = useState(false);
    const [referenceElement, setReferenceElement] = useState<HTMLButtonElement>();
    const [popperElement, setPopperElement] = useState<HTMLDivElement>();
    const { styles, attributes, update } = usePopper(referenceElement, popperElement, {
        placement: placement,
        modifiers: [
            {
                name: 'offset',
                options: {
                    offset: [0, 10]
                }
            }
        ]
    });

    const toggleTimepicker = useCallback(async () => {
        toggle();
        await update();
    }, [update, toggle]);

    const clickOuter = useCallback(
        (e: Event) => {
            const target = e.target as Node;
            if (popperElement?.contains(target) || referenceElement?.contains(target) || !isOpen) {
                return;
            }

            toggle();
        },
        [popperElement, referenceElement, toggle, isOpen]
    );

    // UseEffect when a user click outer of the datepicker when it's open
    useEffect(() => {
        if (!isOpen) {
            document.removeEventListener('mousedown', clickOuter);
            return;
        } else {
            document.addEventListener('mousedown', clickOuter);
        }

        return () => {
            document.removeEventListener('mousedown', clickOuter);
        };
    }, [clickOuter, isOpen]);

    useEffect(() => {
        if (timepickerWrapperRef.current) {
            setTimepickerRefInit(true);
        }
    }, [timepickerWrapperRef]);

    const onTimeChange: DateTimePickerProps['onChange'] = (selectedDates) => {
        onChange(selectedDates[0]);
    };

    return (
        <div>
            {timepickerRefInit ? (
                <Flatpickr
                    ref={timepickerRef}
                    value={time}
                    options={{
                        appendTo: timepickerWrapperRef.current ?? document.body,
                        enableTime: true,
                        noCalendar: true,
                        dateFormat: 'H:i',
                        ...options
                    }}
                    render={(_props, ref) => {
                        if (children) {
                            return children({
                                ref,
                                toggleDatepicker: toggleTimepicker,
                                setReferenceElement
                            });
                        }

                        // The first ref is useful to display the button
                        // Second ref is useful for react-popper to open the datepicker
                        return (
                            <SingleDatepickerInput
                                ref={ref}
                                icon="clock"
                                setReferenceElement={setReferenceElement}
                                toggleDatepicker={toggleTimepicker}
                                id="timepicker"
                                afterInput={afterInput}
                                disabled={disabled}
                            />
                        );
                    }}
                    onChange={onTimeChange}
                />
            ) : null}
            {portalContainer ? (
                ReactDOM.createPortal(
                    <ContainerTimepicker
                        isOpen={isOpen}
                        hasBorder={hasBorder}
                        fullWidth={fullWidth}
                        setPopperElement={setPopperElement}
                        styles={styles}
                        attributes={attributes}
                        timepickerWrapperRef={
                            timepickerWrapperRef as React.MutableRefObject<HTMLDivElement>
                        }
                        referenceElement={referenceElement}
                    />,
                    portalContainer
                )
            ) : (
                <ContainerTimepicker
                    isOpen={isOpen}
                    hasBorder={hasBorder}
                    fullWidth={fullWidth}
                    setPopperElement={setPopperElement}
                    styles={styles}
                    attributes={attributes}
                    timepickerWrapperRef={
                        timepickerWrapperRef as React.MutableRefObject<HTMLDivElement>
                    }
                    referenceElement={referenceElement}
                />
            )}
        </div>
    );
};

type ContainerTimepickerProps = Pick<TimepickerPropsType, 'hasBorder' | 'fullWidth'> & {
    isOpen: boolean;
    setPopperElement: React.Dispatch<React.SetStateAction<Parameters<typeof usePopper>[1]>>;
    styles: ReturnType<typeof usePopper>['styles'];
    attributes: ReturnType<typeof usePopper>['attributes'];
    timepickerWrapperRef: React.MutableRefObject<HTMLDivElement>;
    referenceElement: HTMLButtonElement;
};

const ContainerTimepicker = ({
    isOpen,
    hasBorder,
    fullWidth,
    setPopperElement,
    styles,
    attributes,
    timepickerWrapperRef,
    referenceElement
}: ContainerTimepickerProps) => {
    return (
        <div
            aria-label="timepicker-wrapper"
            ref={setPopperElement}
            className={clsx(
                'flex max-w-[calc(100vw-36px)] overflow-hidden overflow-x-auto rounded-md bg-white shadow-icoM transition-opacity dark:bg-icoDarkMode-deepBlack dark:shadow-icoDarkXL',
                {
                    'h-0 border-0 opacity-0 duration-200 ease-in': !isOpen,
                    'h-auto opacity-100 duration-150 ease-out': isOpen,
                    'border border-icoGray-300 dark:border-icoDarkMode-beton': hasBorder
                }
            )}
            style={styles.popper}
            {...attributes.popper}>
            <div
                ref={timepickerWrapperRef}
                className={clsx({ 'w-full': fullWidth, 'w-80': !fullWidth })}
                style={{ minWidth: referenceElement?.clientWidth }}
            />
        </div>
    );
};

export default Timepicker;
